import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _ArrayUtils;
/*
 *                     NOTICE
 *            COPYRIGHT 2019 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import "@abinitio/kendo.all";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { isObservableArray } from "mobx";
// WARNING: do NOT add DateTimeUtils import as causes circular dependency.  Removed unused toCSV method in ABSDK-3324.

export class ArrayUtils {
  constructor() {}

  /**
   * In es5, array 'wrappers' (such as kendo.data.ObservableArray) return false for Array.isArray(thing) despite
   * implementing the entire Array contract.
   *
   * This method returns true if it is an Array or Array-like, with a corresponding typeguard
   * letting the compiler know that if it returns true then the 'thing' arg implements the Array contract.
   *
   * @param thing
   * @return {thing is Array<any>} returns true iff thing is non-null and satisfies the Array contract.
   */
  static isArrayLike(thing) {
    return thing == null ? false : Array.isArray(thing) || isObservableArray(thing) || ClassUtils.isInstanceOf(thing, kendo.data.ObservableArray);
  }

  /**
   * Same as isArrayLike, but used for readonly array cast.
   */

  /**
   * Return an array of numbers incrementing from start (inclusive) to end (exclusive).
   * If end isn't provided, the range [0, start) is returned.
   * Will error if increment is 0.
   * @param {number} start
   * @param {number} end
   * @param {number} increment (default = 1)
   */
  static range(start, end, increment = 1) {
    if (increment === 0) {
      throw new Error("Cannot create range with increment = 0");
    }
    if (end === undefined) {
      end = start;
      start = 0;
    }
    if (increment > 0 && start >= end || increment < 0 && start <= end) {
      return [];
    }
    const ret = [];
    for (let i = start; increment > 0 ? i < end : i > end; i += increment) {
      ret.push(i);
    }
    return ret;
  }

  /**
   * Produce an array of a given length using function called that many times.
   * @param {number} length
   * @param {(i: number) => T} fun
   * @returns {Array<T>}
   */
  static produceArray(length, fun) {
    let ret = [];
    for (let i = 0; i < length; i++) {
      ret.push(fun(i));
    }
    return ret;
  }

  /**
   * Return a new array, where the i-th element is the result of calling fun(arr1[i], arr2[i]).
   * fun will be passed a pad value of undefined for any unmatched elements of arr2.
   * The resulting array length will be equal to arr1.length.
   * @author jhoel
   */
  static zipPadded(arr1, arr2, fun) {
    const ret = [];
    for (let i = 0; i < arr1.length; i++) {
      const e = fun(arr1[i], i < arr2.length ? arr2[i] : undefined, i);
      ret.push(e);
    }
    return ret;
  }

  /**
   * Return a new array, where the i-th element is the result of calling fun(arr1[i], arr2[i]).
   * fun will be passed a pad value of undefined for any unmatched elements of either arr1 or arr2.
   * The resulting array length will be equal to the longer of arr1 and arr2.
   * @author jhoel
   */
  static zipLongest(arr1, arr2, fun) {
    const ret = [];
    for (let i = 0; i < Math.max(arr1.length, arr2.length); i++) {
      const e = fun(i < arr1.length ? arr1[i] : undefined, i < arr2.length ? arr2[i] : undefined, i);
      ret.push(e);
    }
    return ret;
  }

  /**
   * Return a new array, where the i-th element is the result of calling fun(arr1[i], arr2[i]).
   * fun will be matched elements of arr1 and arr2, never undefined, because the shortest array
   * is the driver of the loop.
   * The resulting array length will be equal to the shorter of arr1 and arr2.
   * @author jhoel
   */
  static zipShortest(arr1, arr2, fun) {
    const ret = [];
    for (let i = 0; i < Math.min(arr1.length, arr2.length); i++) {
      const e = fun(arr1[i], arr2[i], i);
      ret.push(e);
    }
    return ret;
  }

  /**
   * Returns an array with only unique elements inside in order n time assuming the javascript
   * object hash lookup is constant time.  Objects slow down the verification significantly.
   *
   * @param input an array containing anything with a comparison operator
   * @returns {Array<any>} new array of unique elements
   */
  static unique(input, compare) {
    if (!ArrayUtils.isArrayLike(input)) {
      return [];
    }
    const tmp = {};
    const out = [];
    const objects = [];
    for (let i = 0; i < input.length; i++) {
      if (typeof input[i] === "object") {
        if (compare) {
          if (!objects.some(value => compare(input[i], value))) {
            objects.push(input[i]);
            out.push(input[i]);
          }
        } else if (objects.indexOf(input[i]) === -1) {
          objects.push(input[i]);
          out.push(input[i]);
        }
      } else if (tmp[input[i]] === undefined) {
        tmp[input[i]] = true;
        out.push(input[i]);
      }
    }
    return out;
  }

  /**
   * Checks if two arrays have the same contents.
   * Does not check if those contents are in the same order.
   * Treats null and undefined as equivalent.
   * @param array1
   * @param array2
   * @param doesItemExistInOtherArray custom function for figuring out whether an item is contained in the other array
   * @returns {boolean}
   */
  static hasSameContents(array1, array2, doesItemExistInOtherArray = (array, item) => array.indexOf(item) !== -1) {
    // if both are (null or undefined)
    if (array1 == null && array2 == null) return true;

    // if either are (null or undefined)
    if (array1 == null || array2 == null) return false;

    // different length lists are not equal
    if (array1.length !== array2.length) return false;

    // does not compare element order
    for (const item of array1) {
      if (!doesItemExistInOtherArray(array2, item)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if two arrays have the same contents, and in the same order.
   * Treats null and undefined as equivalent.
   * @param array1
   * @param array2
   * @param isItemEqual - optional array item comparison function
   *
   * @returns {boolean}
   */
  static isEquals(array1, array2, isItemEqual = (itemA, itemB) => itemA === itemB) {
    // if both are (null or undefined)
    if (array1 == null && array2 == null) {
      return true;
    }
    // if either are (null or undefined)
    else if (array1 == null || array2 == null) {
      return false;
    }

    // different length lists are not equal
    if (array1.length !== array2.length) {
      return false;
    }

    // does not compare element order
    let i = 0;
    for (const item of array1) {
      //if (array2[i] !== item)
      if (!isItemEqual(array2[i], item)) {
        return false;
      }
      i++;
    }
    return true;
  }

  /**
   * Convert method to fn if thisArg is defined.
   * If thisArg is not provided, assumes that it is already a function
   */
  static convertMethodToFn(fnOrMethod, thisArg) {
    return thisArg !== undefined ? fnOrMethod.bind(thisArg) : fnOrMethod;
  }

  /**
   * Finds the first index in an array that fulfills the provided <code>filter</code> function. This differs from the
   * <code>indexOf</code> method built into Arrays in that it can provide a more complex check than simple equality.
   *
   * @param array           the Array to look in
   * @param filter          the function the execute to determine whether a value meets the desired condition. Its
   *                        signature is <code>(value: T) => boolean</code>
   * @param notFoundValue   the value to return if no items in the array match. Default: -1
   * @returns {number}      the index of the first item in the array that fulfills the <code>filter</code> function, or
   *                        the <code>notFoundValue</code>
   */
  static firstMatchingIndex(array, filter, notFoundValue = -1) {
    const firstIndex = array.map(filter).indexOf(true);
    return firstIndex === -1 ? notFoundValue : firstIndex;
  }

  /**
   * @return {number} index of first element that satisfies filter (-1 if not found)
   */
  static firstIndex(array, filterFnOrMethod, thisArg) {
    const filterFn = ArrayUtils.convertMethodToFn(filterFnOrMethod, thisArg);
    for (let i = 0; i < array.length; i++) {
      if (filterFn(array[i])) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Differs from built-in Array.flat in that it will recurse to infinite depth.
   */
  static flatten(array) {
    let result = new Array();
    array.forEach(entry => {
      if (ClassUtils.isInstanceOf(entry, Array)) {
        const arr = entry;
        result = result.concat(ArrayUtils.flatten(arr));
      } else {
        result.push(entry);
      }
    });
    return result;
  }

  /**
   * Implements functionality of ECMAScript 6 Array.find() which is missing from Internet Explorer.
   * The callback is invoked with three arguments: the value of the element, the index of the element,
   * and the Array object being traversed.
   * Will invoke native ES6 implementation if it is part of the current implementation or simulate if necessary.
   *
   * NOTE: This function will be deprecated when the Array polyfill code is approved by legal.
   *
   * @param array in which to find the element
   * @param func takes element and returns true when there is a match
   * @returns {any} the first value that matches the boolean expression or undefined
   */
  static find(array, func, thisArg, skipArrayCheck = false) {
    const a = Array.prototype;
    if (array == null) {
      return undefined;
    }
    // I believe this is the desired behavior.  Returning undefined would cause issues.
    if (!skipArrayCheck && !ArrayUtils.isArrayLike(array)) {
      throw new Error("Non-Array passed as array to ArrayUtils.find()");
    }

    // Use the built in function if it is available in this browser
    if (a.find) {
      const anyArray = array;
      return anyArray.find(func, thisArg);
    }
    const length = array.length;
    let value;
    for (let i = 0; i < length; i++) {
      value = array[i];
      if (func.call(thisArg, [value, i, array])) {
        return value;
      }
    }
    return undefined;
  }

  /**
   * Count the number of elements in an array that satisfy a supplied predicate.
   */
  static count(arr, predicate) {
    let c = 0;
    for (let x of arr) {
      if (predicate(x)) ++c;
    }
    return c;
  }

  /**
   * Performs numerical sort on an Array of numbers.
   * @param array Array to sort.
   * @param asc true to sort ascending (default), false to sort descending.
   * @returns {Array<number>} Sorted Array.
   */
  static sortNumbers(array, asc = true) {
    const mult = asc ? 1 : -1;
    return array.sort((a, b) => mult * (a - b));
  }

  /**
   * Performs sort on an Array of strings.
   */
  static sortStrings(array) {
    return array.sort((a, b) => {
      if (a > b) return 1;
      if (a < b) return -1;
      return 0;
    });
  }

  /**
   * Remove all instances of val from given array
   * @returns {Array<T>} same array instance, (not a copy) with all instances of val removed
   * Since it returns the same array, you can chain together an additional call.
   */
  static removeAll(array, val) {
    // Array is always non-null (thanks type system!)
    let index = array.indexOf(val);
    while (index !== -1) {
      array.splice(index, 1);
      index = array.indexOf(val, index);
    }
    return array;
  }

  /**
   * Get the last element of a non-empty array.
   */
  static last(arr) {
    Assert.assert(arr.length > 0);
    return arr[arr.length - 1];
  }

  /**
   * Get the first element of a non-empty array.
   */
  static first(arr) {
    Assert.assert(arr.length > 0);
    return arr[0];
  }

  /**
   * Get the first element of an array, or null if the array is empty.
   */
  static firstOrNull(arr) {
    if (arr.length === 0) return null;
    return arr[0];
  }

  /**
   * Get the last element of an array, or null if the array is empty.
   */
  static lastOrNull(arr) {
    if (arr.length === 0) return null;
    return arr[arr.length - 1];
  }

  /**
   * Get the the element of an array, or null if the index is past the end.
   */
  static elementOrNull(arr, i) {
    if (i >= arr.length) return null;
    return arr[i];
  }

  /**
   * Pop the last element of an array, or return null if the array is empty.
   * This has stricter typing than Array.pop, which returns an any.
   */
  static popLast(arr) {
    const ret = this.lastOrNull(arr);
    arr.pop();
    return ret;
  }

  /**
   * Type guard for an array type.
   * @param thing
   * @param predicate
   */

  /**
   * In-place unbiased shuffle of array elements
   * https://stackoverflow.com/questions/2450954/how-to-randomize-shuffle-a-javascript-array
   */
  static shuffle(arr) {
    let currentIndex = arr.length;

    // While there remain elements to shuffle...
    while (currentIndex !== 0) {
      // Pick a remaining element...
      const randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;

      // And swap it with the current element.
      const swap = arr[currentIndex];
      arr[currentIndex] = arr[randomIndex];
      arr[randomIndex] = swap;
      // or use fancy array desctructuring to swap in one line:
      // [arr[currentIndex], arr[randomIndex]] = [arr[randomIndex], arr[currentIndex]];
    }
  }

  /**
   * Intersperses a value in between exiting values in an array. For example,
   * ["a", "b", "c"] => ["a", "separator", "b", "separator", "c"]
   * the intersperseItemFunc provides the index as an argument for key purposes when working with react nodes.
   */
  static intersperse(arr, intersperseItemFunc) {
    return arr.flatMap((v, i) => [v, intersperseItemFunc(2 * i + 1)]).slice(0, -1);
  }

  /**
   * Joins a list of strings and escapes existing usages of the join character and the escape character. For
   * example, if the join character is a period, it would turn ["hi.there", "example] into "hi\.there.example"
   * which ensures two arrays make the same string if and only if the arrays were equal pairwise before the
   * transformation.
   */
  static safeJoin(arr, joinChar = "/", escapeChar = "\\") {
    if (joinChar === escapeChar) throw new Error(`Join character '${joinChar}' must be different from escape character '${escapeChar}'`);
    if (joinChar.length !== 1) throw new Error(`Join character '${joinChar}' must have length 1.`);
    if (escapeChar.length !== 1) throw new Error(`Escape character '${escapeChar}' must have length 1.`);
    return arr.map(item => item.replaceAll(escapeChar, `${escapeChar}${escapeChar}`).replaceAll(joinChar, `${escapeChar}${joinChar}`)).join(joinChar);
  }

  /**
   * inverse of safeJoin.
   */
  static safeSplit(s, joinChar = "/", escapeChar = "\\") {
    if (joinChar === escapeChar) throw new Error(`Join character '${joinChar}' must be different from escape character '${escapeChar}'`);
    if (joinChar.length !== 1) throw new Error(`Join character '${joinChar}' must have length 1.`);
    if (escapeChar.length !== 1) throw new Error(`Escape character '${escapeChar}' must have length 1.`);
    const toks = [];
    let prevIndex = 0;
    let index = 0;
    let tok = "";
    while (index < s.length) {
      const currChar = s[index];
      if (currChar === escapeChar) {
        tok += s[index + 1];
        index += 2;
        continue;
      }
      if (currChar === joinChar) {
        toks.push(tok);
        tok = "";
        prevIndex = ++index;
        continue;
      }
      tok += s[index];
      index++;
    }
    toks.push(tok);
    return toks;
  }
}
_ArrayUtils = ArrayUtils;
_defineProperty(ArrayUtils, "isReadonlyArrayLike", _ArrayUtils.isArrayLike);
_defineProperty(ArrayUtils, "isArrayOf", (thing, predicate) => {
  return _ArrayUtils.isArrayLike(thing) && thing.every(predicate);
});
/**
 * Type guard for string array.
 * @param thing
 */
_defineProperty(ArrayUtils, "isStringArray", thing => {
  return _ArrayUtils.isArrayOf(thing, item => typeof item === "string");
});
/**
 * Filter out null elements from this list.
 * @param list
 */
_defineProperty(ArrayUtils, "removeNulls", list => {
  return list.filter(value => value !== null);
});
_defineProperty(ArrayUtils, "moveArrayItem", (list, event) => {
  if (event.fromIndex < 0 || event.fromIndex >= list.length || event.toIndex < 0 || event.toIndex >= list.length) throw new Error(`Indices must be >= 0 and < length of array (${list.length}). fromIndex: ${event.fromIndex}, toIndex: ${event.toIndex}`);
  const copy = [...list];
  if (event.fromIndex === event.toIndex) return copy;
  const fromElement = copy.splice(event.fromIndex, 1)[0];
  copy.splice(event.toIndex, 0, fromElement);
  return copy;
});
//# sourceMappingURL=ArrayUtils.js.map
